import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { authHttpInterceptorFn } from '@auth0/auth0-angular';
import { ENV } from '@bodyanalytics/app-config';
import { ApiLocalRoutesEnum } from '@bodyanalytics/ui-core';
import { EmbeddedSessionService } from './embedded-session.service';

/**
 * Drop-in replacement for auth0-angular's `authHttpInterceptorFn` in the three
 * portal `app.config.ts` files (embedded ROPG login, ADR-0033).
 *
 * `AuthHttpInterceptor` talks to the auth0-spa-js client directly (it does NOT
 * go through `AuthService`, so the DI bridge can't cover it): with an embedded
 * session and no SDK session, every bare-string allowlist match would fail its
 * silent token fetch and throw (#2056 failure mode). So:
 *
 * - live embedded session → attach its bearer to own-API requests, bypass the
 *   SDK interceptor entirely;
 * - expired embedded session with a refresh token → silently refresh once
 *   (concurrent requests share the in-flight exchange), then attach; if the
 *   refresh fails the session is cleared and the request falls through to the
 *   stock SDK interceptor;
 * - no embedded session (flag-off world, signed-out visitors) → delegate to
 *   the stock `authHttpInterceptorFn` + allowedList unchanged.
 *
 * Scope guards: tokens are only attached to requests for this app's own API
 * base, and `auth-session-api/*` calls are passed straight through — the
 * refresh POST itself must never trigger another refresh (recursion).
 */
export const embeddedAwareAuthHttpInterceptorFn: HttpInterceptorFn = (request, next) => {
  const embeddedSession = inject(EmbeddedSessionService);
  const appConfig = inject(ENV);
  // Captured NOW (inside the injection context): the refresh-failure fallback
  // below runs asynchronously, where a bare authHttpInterceptorFn() call would
  // throw NG0203 (inject() outside an injection context).
  const injector = inject(EnvironmentInjector);

  const isAuthSessionCall = request.url.includes(`${ApiLocalRoutesEnum.API_SUFFIX_AUTH_SESSION_LOGIN.split('/')[0]}/`);
  if (isAuthSessionCall) {
    return next(request);
  }

  const attach = (req: HttpRequest<unknown>, token: string) =>
    req.url.startsWith(appConfig.api) && !req.headers.has('Authorization')
      ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
      : req;

  const token = embeddedSession.accessToken;
  if (token) {
    return next(attach(request, token));
  }

  if (embeddedSession.canRefresh && request.url.startsWith(appConfig.api)) {
    return embeddedSession.refresh().pipe(
      switchMap(session => next(attach(request, session.accessToken))),
      // Refresh failed (session already cleared by the service): fall through
      // to the stock SDK interceptor exactly like a signed-out visitor.
      catchError(() => runInInjectionContext(injector, () => authHttpInterceptorFn(request, next))),
    );
  }

  return authHttpInterceptorFn(request, next);
};
